import { useState } from "react";

import {
  SeletorEscopo,
  type EscopoSelecionado,
} from "../projecao/SeletorEscopo";
import { TabelaProjecao } from "../projecao/TabelaProjecao";
import { ModalComposicaoDia } from "../projecao/ModalComposicaoDia";
import { Procedencia } from "../projecao/Procedencia";
import { ControleHorizonte } from "../projecao/ControleHorizonte";
import {
  useProjecaoEmpresa,
  useProjecaoGrupo,
} from "../../lib/projecao/hooks";
import { useTemProjecaoIrrestrita } from "../../lib/auth/permissoes";
import { HORIZONTE_DEFAULT } from "../../lib/projecao/tipos";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Página de projeção de fluxo de caixa (FE-2 CP-C).
 *
 * Estado local (client-state, Zustand não é necessário aqui — é estado de tela
 * efêmero, não compartilhado): o escopo selecionado (empresa id | grupo) e o
 * HORIZONTE (1..365, default `HORIZONTE_DEFAULT`). O `ControleHorizonte` mantém
 * o horizonte saneado; ele propaga para AMBOS os ramos (empresa e grupo) — como
 * o horizonte está na queryKey (CP-A), trocá-lo refaz a query.
 *
 * ESCOPO "empresa:{id}": chama `useProjecaoEmpresa(id, horizonte)` e renderiza
 * `Procedencia` + `TabelaProjecao`.
 *
 * ESCOPO "grupo": chama `useProjecaoGrupo(horizonte, habilitado)` e renderiza só
 * a `TabelaProjecao` (T5 — o grupo NÃO tem procedência; o tipo `ProjecaoGrupoOut`
 * nem a expõe). `habilitado = useTemProjecaoIrrestrita()`: o ramo grupo SÓ
 * dispara o fetch para usuário irrestrito. DUPLA PROTEÇÃO: o `SeletorEscopo` já
 * não oferece a opção grupo a escopado, E o `enabled` do hook fica false para
 * não-irrestrito (defesa-em-profundidade caso o ramo fosse alcançado). O backend
 * permanece o gate final (403).
 *
 * ESTADOS de erro (sinais tipados do `ErroHttp`):
 *  - 503 (`moduloIndisponivel`, flag dormente OFF) → mensagem de indisponível.
 *  - 404 (`NaoEncontrado`) → mensagem genérica (anti-enumeração, espelha a borda).
 * A sessão expirada é tratada pelo interceptor (redireciona) — não cai aqui.
 */
export function Projecao(): JSX.Element {
  const [escopo, setEscopo] = useState<EscopoSelecionado | null>(null);
  const [horizonte, setHorizonte] = useState<number>(HORIZONTE_DEFAULT);
  const irrestrito = useTemProjecaoIrrestrita();

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-titulo text-2xl text-off-black">Projeção</h1>

      <div className="flex flex-wrap items-end gap-6">
        <SeletorEscopo selecionado={escopo} aoSelecionar={setEscopo} />
        <ControleHorizonte valor={horizonte} aoMudar={setHorizonte} />
      </div>

      {escopo === null && (
        <p className="font-corpo text-base text-off-black">
          Selecione um escopo para ver a projeção.
        </p>
      )}

      {escopo?.tipo === "grupo" && (
        <VisaoGrupo horizonte={horizonte} habilitado={irrestrito} />
      )}

      {escopo?.tipo === "empresa" && (
        <VisaoEmpresa empresaId={escopo.empresaId} horizonte={horizonte} />
      )}
    </section>
  );
}

interface VisaoEmpresaProps {
  empresaId: string;
  horizonte: number;
}

/**
 * Visão por-empresa: dispara `useProjecaoEmpresa` e renderiza procedência +
 * tabela. Componente próprio para que o hook só seja montado quando uma empresa
 * está de fato selecionada (não há fetch ocioso no ramo grupo/sem-seleção).
 *
 * DRILL-DOWN ITEMIZADO (FE-8): clicar um dia da tabela seleciona-o
 * (`diaSelecionado`) e abre o `ModalComposicaoDia` com a composição do dia (BE
 * gap A). É EXCLUSIVO da visão empresa — o grupo (`VisaoGrupo`) mantém o
 * drill-down raso (passa a tabela SEM `aoAbrirComposicao`).
 */
function VisaoEmpresa({ empresaId, horizonte }: VisaoEmpresaProps): JSX.Element {
  const consulta = useProjecaoEmpresa(empresaId, horizonte);
  const [diaSelecionado, setDiaSelecionado] = useState<string | null>(null);

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando projeção…
      </p>
    );
  }

  if (consulta.isError) {
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagemErro(consulta.error)}
      </p>
    );
  }

  const projecao = consulta.data;

  return (
    <div className="flex flex-col gap-4">
      <Procedencia procedencia={projecao.procedencia} />
      <TabelaProjecao
        serie={projecao.serie}
        aoAbrirComposicao={setDiaSelecionado}
      />
      {diaSelecionado !== null && (
        <ModalComposicaoDia
          empresaId={empresaId}
          data={diaSelecionado}
          horizonte={horizonte}
          aoFechar={() => setDiaSelecionado(null)}
        />
      )}
    </div>
  );
}

interface VisaoGrupoProps {
  horizonte: number;
  habilitado: boolean;
}

/**
 * Visão de GRUPO (consolidado): dispara `useProjecaoGrupo(horizonte, habilitado)`
 * e renderiza SÓ a `TabelaProjecao` (reusada do ramo empresa) — sem `Procedencia`,
 * que não existe no contrato do grupo (T5). `habilitado` amarra o fetch à
 * irrestrição: false → o hook fica `enabled: false` e não há fetch.
 *
 * Quando `habilitado` é false a query nunca resolve (`isPending` perpétuo); na
 * prática o `SeletorEscopo` impede chegar aqui sem irrestrição, então a mensagem
 * de loading não é observável em uso normal — mas o componente permanece correto
 * (defesa-em-profundidade) ao não disparar a query.
 */
function VisaoGrupo({ horizonte, habilitado }: VisaoGrupoProps): JSX.Element {
  const consulta = useProjecaoGrupo(horizonte, habilitado);

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando projeção…
      </p>
    );
  }

  if (consulta.isError) {
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagemErro(consulta.error)}
      </p>
    );
  }

  const projecao = consulta.data;

  return (
    <div className="flex flex-col gap-4">
      <TabelaProjecao serie={projecao.serie} />
    </div>
  );
}

/**
 * Traduz o erro da consulta numa mensagem estável. 503 → indisponível; 404 →
 * genérico; demais casos reusam a mensagem do `ErroHttp`. Erro não-`ErroHttp`
 * (improvável; defesa) → fallback genérico. Compartilhado entre os ramos empresa
 * e grupo (mesma semântica de mensagens — DRY sem mudança de comportamento).
 */
function mensagemErro(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.moduloIndisponivel) {
      return "Módulo de projeção indisponível no momento.";
    }
    return erro.message;
  }
  return "Não foi possível carregar a projeção.";
}
